import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { PublicSamWidget } from "@/components/PublicSamWidget";
import { PublicPromoBadge } from "@/components/PublicPromoBadge";
import { ROICalculator } from "@/components/ROICalculator";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight, Sparkles, Minus, Plus } from "lucide-react";

const INCLUDED = [
  "Everything included, day one",
  "Unlimited AI chat assistant",
  "Smart contact management & enrichment",
  "Multi-step follow-up sequences",
  "Content drafting that sounds like you",
  "Site chatbots that capture real leads",
  "Knowledge base in your own words",
  "Daily briefing from your executive assistant",
  "No upsells. No implementation fees. No surprises.",
  "+ $9 per additional seat / month",
  "Priority email support, real humans",
  "Fully reversible — switch back anytime",
];

const FAQ = [
  { q: "Can I cancel anytime?", a: "Yes. There are no contracts. Cancel from your Billing tab and you keep access until the end of the current period. Your data is yours — export it anytime." },
  { q: "Is the free trial really free?", a: "Yes. 7 days, no credit card. You can use every module before deciding. We'll send a friendly reminder before the trial ends." },
  { q: "What is the FOUNDER19 promo code?", a: "FOUNDER19 takes $10 off the base seat every month, for life. You lock in at $19/mo (instead of $29) for as long as your subscription stays active. Founders-only — first-time customers, while spots last. The discount applies to the base seat only; additional seats stay at $9/mo each." },
  { q: "What if I have a tough month and can't pay?", a: "Just let us know. We'll pause things and pick back up next month. No collections, no late fees, no awkwardness. We've all been there." },
  { q: "Will you raise my price later?", a: "No. If we change our public pricing, you stay grandfathered at the rate you signed up at. Forever." },
  { q: "Are there any implementation, upgrade, or service fees?", a: "No. The price you see is the only price you'll ever pay. No hidden charges, no surprises." },
  { q: "What if I decide it's not for me?", a: "Switch back to your old systems anytime. No penalties, no broken contracts, no data lock-in. We'd rather you leave happy than stay stuck." },
  { q: "Are there any hidden fees?", a: "No. $29/month for the owner seat. $9/month per additional teammate. That's it. No setup fees. No implementation fees. No 'enterprise' tier hiding behind a sales call." },
  { q: "Do I need to replace my existing tools?", a: "No. We work alongside what you've already built — your CRM, your inbox, your spreadsheets. We improve what works. We don't force you to start over." },
  { q: "How does seat pricing work?", a: "The base plan is $29/month and includes the owner seat. Each additional teammate is $9/month, billed on the same cycle. Add or remove seats anytime in Settings → Team." },
  { q: "What kind of support do I get?", a: "Priority email support from real humans is included for every customer. No chatbots, no tier wall, no upsell mid-ticket." },
];

export default function Pricing() {
  const [seats, setSeats] = useState(1);
  const baseTotal = 29;
  const perSeat = 9;
  const additionalSeats = Math.max(0, seats - 1);
  const monthlyTotal = baseTotal + additionalSeats * perSeat;
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050505] text-white">
      <div className="aurora opacity-60" />
      <div className="grain" />
      <PublicNav />

      <section className="relative">
        <div className="mx-auto max-w-5xl px-6 pt-16 pb-12 sm:px-8 sm:pt-24 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Pricing</div>
            <h1 className="font-display mt-3 text-5xl font-light tracking-tight sm:text-6xl" data-testid="pricing-headline">
              One simple price. <span className="brand-text">Everything included.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600 dark:text-white/65" data-testid="pricing-sub">
              Grounded AI assistants, embeddable agents, content generation and a knowledge base — all included. Cancel anytime. Switch back to your old systems if it's not for you. No penalties, ever.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-white/55" data-testid="pricing-trust-pills">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> One simple price</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No contracts</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No upsells</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Cancel anytime</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-5xl px-6 pb-24 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="glass-heavy relative overflow-hidden rounded-3xl p-10 sm:p-14"
            data-testid="pricing-card"
          >
            <div className="absolute inset-x-0 top-0 h-1 brand-bg" />
            <div className="grid gap-12 md:grid-cols-2">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-900/10 bg-slate-50/80 px-3 py-1 text-xs text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-white/70">
                  <Sparkles className="h-3.5 w-3.5 text-indigo-500" /> Symplee · everything included
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <div className="font-display text-7xl font-light tracking-tight">$29</div>
                  <div className="text-lg text-white/40">/month</div>
                </div>
                <p className="mt-2 text-sm text-white/55" data-testid="pricing-seat-sub">includes the owner seat</p>
                <p className="mt-3 text-sm text-indigo-600/85 dark:text-indigo-300/85" data-testid="pricing-extra-seat-note">
                  + $9 per additional seat / month
                </p>

                {/* Seat stepper preview */}
                <div className="mt-7 rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="seat-stepper">
                  <div className="text-eyebrow text-white/40">Estimate your total</div>
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={() => setSeats((s) => Math.max(1, s - 1))}
                      className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 disabled:opacity-30"
                      disabled={seats <= 1}
                      data-testid="seat-stepper-dec"
                      aria-label="Remove seat"
                    >
                      <Minus className="mx-auto h-3.5 w-3.5" />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="font-display text-2xl font-light text-white" data-testid="seat-count">{seats}</div>
                      <div className="text-[10px] uppercase tracking-wider text-white/40">seat{seats > 1 ? "s" : ""}</div>
                    </div>
                    <button
                      onClick={() => setSeats((s) => Math.min(50, s + 1))}
                      className="h-9 w-9 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10"
                      data-testid="seat-stepper-inc"
                      aria-label="Add seat"
                    >
                      <Plus className="mx-auto h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="mt-3 flex items-baseline justify-between border-t border-white/5 pt-3 text-sm">
                    <span className="text-white/55">
                      $29 {additionalSeats > 0 && <span>+ {additionalSeats} × $9</span>}
                    </span>
                    <span className="font-display text-2xl font-light text-white" data-testid="seat-total">
                      ${monthlyTotal}<span className="text-sm text-white/45">/mo</span>
                    </span>
                  </div>
                </div>

                <Link to="/signup" data-testid="pricing-cta">
                  <Button className="mt-8 h-12 w-full rounded-full bg-white text-base text-black hover:bg-zinc-200 sm:w-auto sm:px-9">
                    Start your 7-day free trial <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <p className="mt-4 text-xs text-white/40">
                  No credit card · No contracts · Fully reversible
                </p>
                <PublicPromoBadge />
              </div>

              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 md:col-span-1">
                {INCLUDED.map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" /> {i}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Iter 25 (D2) — Inline ROI calculator. Lives between the pricing
          card and the "Our Promise" section so visitors can convert
          their abstract pricing concern into a concrete savings number
          before they read the long-form trust copy. */}
      <section className="relative" data-testid="pricing-roi-section">
        <div className="mx-auto max-w-5xl px-6 pb-12 sm:px-8 lg:px-12">
          <ROICalculator />
        </div>
      </section>

      {/* Our Promise callout — sits between plan card and FAQ */}
      <section className="relative" data-testid="pricing-promise-section">
        <div className="mx-auto max-w-3xl px-6 pb-20 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="font-display text-4xl font-light tracking-tight sm:text-5xl" data-testid="pricing-promise-heading">
              Our Promise
            </h2>
            <p className="mt-3 text-base italic text-white/55" data-testid="pricing-promise-subhead">
              Plain and honest. No fine print.
            </p>
          </div>

          <div className="mx-auto mt-10 max-w-2xl space-y-6 text-center text-lg leading-[1.75] text-white/75">
            <p><strong className="font-medium text-white">No contracts</strong> &mdash; cancel anytime, no questions asked.</p>
            <p><strong className="font-medium text-white">No late fees, no collections, no threats</strong> &mdash; short on cash one month? Just tell us, we&rsquo;ll pick things up next month.</p>
            <p><strong className="font-medium text-white">No hidden charges</strong> &mdash; no implementation fees, no upgrade fees, no service fees.</p>
            <p><strong className="font-medium text-white">Price locked for life</strong> &mdash; if rates go up, you stay grandfathered. No surprises, ever.</p>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/promise"
              className="text-sm text-indigo-300 underline-offset-4 hover:underline"
              data-testid="pricing-promise-link"
            >
              Read our full promise &rarr;
            </Link>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="mx-auto max-w-3xl px-6 pb-32 sm:px-8 lg:px-12">
          <div className="text-eyebrow text-indigo-600 dark:text-indigo-300">Frequently asked</div>
          <h2 className="font-display mt-3 text-4xl font-light tracking-tight sm:text-5xl">Questions, answered honestly.</h2>

          <Accordion type="single" collapsible className="mt-10" data-testid="pricing-faq">
            {FAQ.map((f, idx) => (
              <AccordionItem key={f.q} value={`f${idx}`} className="border-white/10">
                <AccordionTrigger className="text-left font-display text-base font-medium hover:text-white">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/60">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
      <PublicSamWidget />
    </div>
  );
}
